/**
 * The functional core of the layer controls: applies visibility, opacity, and
 * the swipe clip to the deck.gl layer array as the last transform before it
 * reaches DeckGL.
 *
 * The deck.gl layer tree HandleLayers builds is DEEPLY NESTED (arrays of
 * arrays, plus `null`/`false` entries) -- deck flattens it internally, but our
 * overrides must too or they never reach the real Layer instances. So we
 * recursively flatten to a flat `Layer[]` first, then clone-apply the
 * overrides. deck.gl accepts a flat array; order is preserved.
 */
import { Layer } from '@deck.gl/core';
import type { LayerExtension, LayerProps } from '@deck.gl/core';
import { ClipExtension } from '@deck.gl/extensions';
import {
  groupKeyForLeaf,
  isEventLeaf,
  isLayerInSwipeSet,
  isLeafShownIn,
  leafForKey,
  leafKeyForLayerId,
} from './layerRegistry';
import { clamp01, type SwipeDrag } from './atoms';

/** Reused for every clipped layer -- a single stateless extension instance. */
const clip = new ClipExtension();

/** clipBounds that clip nothing. Event layers carry the ClipExtension from
 * the moment they mount, with these bounds while no swipe is active: deck
 * builds a layer's shaders once at initialization, so an extension added to
 * an already-mounted layer does not take effect, while updating clipBounds is
 * a plain uniform change that always does. */
const UNCLIPPED_BOUNDS: [number, number, number, number] = [-180, -90, 180, 90];

const LNG_MIN = -180;
const LNG_MAX = 180;
/** Web Mercator cuts off around this latitude, so a directional clip bounded
 * by it spans the whole visible map. */
const MERCATOR_LAT_LIMIT = 85;

export type SwipeState = {
  enabled: boolean;
  drag: SwipeDrag | null;
  /** Which event leaves the swipe clips, keyed by leaf key (missing => in). */
  layers: Record<string, boolean>;
};

export type LayerOverrideInput = {
  /** Per-layer visibility, keyed by leaf key (missing => visible). */
  leafVisibility: Record<string, boolean>;
  /** Per-group visibility, keyed by group key (missing => visible). */
  groupVisibility: Record<string, boolean>;
  /** Per-group opacity, keyed by group key (missing => 1). */
  groupOpacity: Record<string, number>;
  /** Per-layer opacity, keyed by leaf key (missing => 1). */
  leafOpacity: Record<string, number>;
  swipe: SwipeState;
};

/**
 * Recursively flatten the nested deck.gl layer tree into a flat array of real
 * Layer instances, dropping `null` / `false` / `undefined` and descending into
 * arrays-of-arrays. A "real Layer" is anything exposing a `clone` method.
 */
export function flattenLayers(layers: unknown): Layer[] {
  const out: Layer[] = [];
  const walk = (node: unknown): void => {
    if (node == null || node === false) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof (node as { clone?: unknown }).clone === 'function') {
      out.push(node as Layer);
    }
  };
  walk(layers);
  return out;
}

/**
 * From the flattened RAW layers (before any override), the set of leaf keys
 * that are actually present on the map: a leaf is present iff at least one
 * matched layer has `props.visible !== false`.
 */
export function computePresentLeaves(flatLayers: Layer[]): string[] {
  const present = new Set<string>();
  for (const layer of flatLayers) {
    const leafKey = leafKeyForLayerId(layer.id);
    if (leafKey == null) continue;
    if (layer.props.visible !== false) present.add(leafKey);
  }
  return Array.from(present);
}

/**
 * Convert the swipe split into `clipBounds` for the SELECTED event leaves. The
 * split `coord` is a GEOGRAPHIC anchor (longitude for the horizontal axis,
 * latitude for the vertical), so the clip is independent of the view state --
 * panning/zooming never moves it. (The bounds are geo-axis-aligned, so with a
 * rotated/pitched view the clip edge and the on-screen divider would diverge;
 * the map is north-up.) The layer is hidden on the side the cursor came FROM
 * (basemap revealed there), so the drag DIRECTION picks the kept side:
 *   horizontal dir +1 (drag right) -> keep EAST of the split  (hide west)
 *   horizontal dir -1 (drag left)  -> keep WEST of the split  (hide east)
 *   vertical   dir +1 (drag down)  -> keep SOUTH of the split (hide north)
 *   vertical   dir -1 (drag up)    -> keep NORTH of the split (hide south)
 * Returns `null` if no usable clip.
 */
function swipeClipBounds(swipe: SwipeState): [number, number, number, number] | null {
  if (!swipe.enabled || swipe.drag == null) return null;

  const { axis, coord, dir } = swipe.drag;
  if (!Number.isFinite(coord)) return null;

  if (axis === 'horizontal') {
    // dir +1: keep east [coord, LNG_MAX]; dir -1: keep west [LNG_MIN, coord].
    return dir === 1
      ? [coord, -MERCATOR_LAT_LIMIT, LNG_MAX, MERCATOR_LAT_LIMIT]
      : [LNG_MIN, -MERCATOR_LAT_LIMIT, coord, MERCATOR_LAT_LIMIT];
  }

  // Screen y grows downward (lower latitude). dir +1 (drag down): keep south
  // of the split; dir -1 (drag up): keep north.
  return dir === 1
    ? [LNG_MIN, -MERCATOR_LAT_LIMIT, LNG_MAX, coord]
    : [LNG_MIN, coord, LNG_MAX, MERCATOR_LAT_LIMIT];
}

export function applyLayerOverrides(layers: unknown, input: LayerOverrideInput): Layer[] {
  const { leafVisibility, groupVisibility, groupOpacity, leafOpacity, swipe } = input;

  const clipBounds = swipeClipBounds(swipe);
  const flat = flattenLayers(layers);

  return flat.map(layer => {
    const leafKey = leafKeyForLayerId(layer.id);
    if (leafKey == null) return layer;
    const leaf = leafForKey(leafKey);
    if (leaf == null) return layer;
    const groupKey = groupKeyForLeaf(leafKey);

    const changes: Record<string, unknown> = {};

    // A layer renders only when its group AND its own eye are on (see
    // isLeafShownIn for the app-controlled boundary leaves, whose own eye is
    // the app atom and must not be fought here -- or hide incident-page
    // county fills).
    if (!isLeafShownIn(leafKey, leafVisibility, groupVisibility)) {
      if (leaf.hide != null) {
        const lower = layer.id.toLowerCase();
        if (leaf.hide.patterns.some(p => lower.includes(p.toLowerCase()))) {
          Object.assign(changes, leaf.hide.props);
        }
      } else {
        changes.visible = false;
      }
    }

    // Effective opacity is the PRODUCT group * layer, scaled onto whatever
    // opacity the layer itself renders at, so both sliders always have an
    // effect and 100% leaves the layer's own value untouched.
    const effective =
      clamp01(groupKey != null ? (groupOpacity[groupKey] ?? 1) : 1) *
      clamp01(leafOpacity[leafKey] ?? 1);
    if (effective !== 1) {
      changes.opacity = layer.props.opacity * effective;
    }

    // Clip the SELECTED event layers along the drag axis (ArcGIS-style swipe).
    // Every event layer always carries the extension (see UNCLIPPED_BOUNDS);
    // only the bounds change when a swipe engages or its membership toggles.
    if (isEventLeaf(leafKey)) {
      const existing = (layer.props.extensions ?? []) as LayerExtension[];
      changes.extensions = [...existing, clip];
      changes.clipBounds =
        clipBounds != null && isLayerInSwipeSet(leafKey, swipe.layers)
          ? clipBounds
          : UNCLIPPED_BOUNDS;
    }

    return Object.keys(changes).length > 0
      ? (layer.clone(changes as Partial<LayerProps>) as Layer)
      : layer;
  });
}
